import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Eye, ArrowLeft, Shield } from 'lucide-react';
import { toast } from 'sonner';

export function ImpersonationBanner() {
  const [impersonationData, setImpersonationData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const sessionId = localStorage.getItem('impersonation_session');
    const targetUser = localStorage.getItem('impersonation_target');

    if (sessionId && targetUser) {
      try {
        setImpersonationData({
          sessionId,
          targetUser: JSON.parse(targetUser),
        });
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem('impersonation_session');
        localStorage.removeItem('impersonation_target');
      }
    }
  }, []);

  const stopImpersonation = async () => {
    setIsLoading(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      await supabase.functions.invoke(
        'admin-user-management?operation=impersonate-stop',
        {
          headers: { Authorization: `Bearer ${token}` },
          body: { session_id: impersonationData.sessionId },
        }
      );

      // Sign out from impersonated account
      await supabase.auth.signOut();

      // Clear impersonation data
      localStorage.removeItem('impersonation_session');
      localStorage.removeItem('impersonation_target');
      
      toast.success('Returned to admin account');
      
      // Redirect to login page so admin can log back in
      const returnUrl = localStorage.getItem('admin_return_url') || '/admin/users';
      localStorage.removeItem('admin_return_url');
      window.location.href = '/auth';
    } catch (error: any) {
      toast.error('Failed to stop impersonation');
      setIsLoading(false);
    }
  };

  if (!impersonationData) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
              <Eye className="h-4 w-4" />
              <span className="text-sm font-medium">Impersonation Mode</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="text-sm">
                Viewing as: <strong>{impersonationData.targetUser.email}</strong>
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={stopImpersonation}
            disabled={isLoading}
            className="bg-white text-orange-600 hover:bg-white/90 gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {isLoading ? 'Returning...' : 'Return to Admin'}
          </Button>
        </div>
      </div>
    </div>
  );
}
